




























































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import router from "@/router/router";
import { PdfTemplateRow } from "@/data/models/ClientAdministrationRow";
import {
  cachedClientForAdministration,
  copyClientPdfTemplateForAdministration,
  getClientForAdministration,
  getClientPdfTemplatesForAdministration,
  uploadClientPdfTemplateForAdministration,
} from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import PdfTemplateFileDialogComponent from "@/components/admin/clients/PdfTemplateFileDialog.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import PhoneEditingHintComponent from "@/components/admin/PhoneEditingHint.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";
import { clientDetailTitle, EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { rememberClientTitle, setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import {
  fileActionConsequences,
  fileOperationErrorKey,
  PdfTemplateFileAction,
  readFileAsBase64,
} from "@/utils/PdfTemplateFiles";
import { isIgnoredCertificateTemplate, selectPdfTemplateRows } from "@/utils/ClientPdfTemplateList";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * PDF templates (PDF-Vorlagen) in the admin portal: a client's templates, or the shared ones
 * (`shared=1`), with how many fields each maps, which template the HyDry certificate uses, and for
 * that one how many mapped values IRES Cloud leaves blank. A row opens the template's mapping editor.
 *
 * Opened from the client detail; the shared list from any client's list (its `clientId` kept for the
 * way back).
 *
 * **Not the access boundary**: the data comes from `getClientPdfTemplatesForAdministration`, which
 * repeats the portal and `MANAGE_CLIENTS` checks server-side. NEUE VORLAGE and AUS GETEILTEN
 * ÜBERNEHMEN write through their own server-checked callables, and open the new template.
 */
@Component({
  // Explicit, so Scaffold's keep-alive still matches it once the build minifies the class name.
  name: "ClientAdministrationPdfTemplatesComponent",
  components: {
    "file-dialog": PdfTemplateFileDialogComponent,
    "content-state": ContentStateComponent,
    "phone-editing-hint": PhoneEditingHintComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
  },
})
export default class ClientAdministrationPdfTemplatesComponent extends Vue {
  @Ref("fileDialog")
  fileDialog!: PdfTemplateFileDialogComponent;

  templates: PdfTemplateRow[] = [];
  busy = false;
  /** The client's name for the dialogs' consequence lines; null for shared or when unknown. */
  clientLabel: string | null = null;
  readonly emptyValue = EMPTY_VALUE;
  hyDryCertificateTemplateId: string | null = null;
  isLoading = true;
  loadError = false;
  searchQuery = "";
  /** The template opened last, read once from session storage. */
  lastVisitedKey: string | null = null;
  /** False while kept alive but not shown; gates the app bar portal. */
  componentActive = false;

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  /** Below 600 px: the compact header (`AdminRefreshButton`, the ⋮ menu) and no column headings. */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get clientId(): string | null {
    const value = this.$route.query.clientId;
    return typeof value === "string" && value !== "" ? value : null;
  }

  get isShared(): boolean {
    return this.$route.query.shared === "1";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get rows(): PdfTemplateRow[] {
    return selectPdfTemplateRows(this.templates, this.searchQuery, this.hyDryCertificateTemplateId);
  }

  visitedKey(template: PdfTemplateRow): string {
    return `ADMIN_PDF_TEMPLATES/${this.isShared ? "shared" : this.clientId}/${template.id}`;
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(template: PdfTemplateRow, hover: boolean): string {
    const hoverBackground = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";

    if (hoverBackground) {
      return `background-color: ${hoverBackground}`;
    }

    return this.visitedKey(template) === this.lastVisitedKey ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  isIgnored(template: PdfTemplateRow): boolean {
    return isIgnoredCertificateTemplate(template, this.hyDryCertificateTemplateId);
  }

  /**
   * Values IRES Cloud leaves blank, counted on the HyDry certificate's template only: IRES Cloud fills
   * no other template (IRES Project does), so there the count would flag values that print fine.
   */
  problemCount(template: PdfTemplateRow): number {
    return template.id === this.hyDryCertificateTemplateId ? template.problemValueCount : 0;
  }

  problemLabel(template: PdfTemplateRow): string {
    const count = this.problemCount(template);
    return count === 0 ? "–" : this.$tc("admin.pdfTemplates.problemValues", count).toString();
  }

  formatDate(millis: number | null): string {
    return millis === null ? "–" : formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  openTemplate(template: PdfTemplateRow) {
    this.lastVisitedKey = this.visitedKey(template);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_2);

    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATE,
      query: { ...this.scopeQuery, templateId: template.id },
    });
  }

  consequences(action: PdfTemplateFileAction): string[] {
    return fileActionConsequences(action, {
      isShared: this.isShared,
      clientName: this.clientLabel || this.clientId || EMPTY_VALUE,
      isCertificateTemplate: false,
      templateName: "",
    }).map((line) => this.$t(line.key, line.params).toString());
  }

  /** Opens a template just made here, by id. */
  openTemplateById(templateId: string) {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATE, query: { ...this.scopeQuery, templateId } });
  }

  /** A new template from a PDF under a name new to this scope, then its page to link the fields. */
  async onUpload() {
    const choice = await this.fileDialog.show({
      mode: "upload",
      title: this.$t("admin.pdfTemplates.files.uploadTitle").toString(),
      initialName: "",
      takenFileNames: this.templates.map((template) => template.fileName ?? ""),
      consequenceLines: this.consequences("upload"),
    });
    if (!choice?.file) {
      return;
    }

    const file = choice.file;
    await this.runAndOpen(async () =>
      uploadClientPdfTemplateForAdministration(this.isShared ? null : this.clientId, choice.fileName, await readFileAsBase64(file))
    );
  }

  /** A shared template copied into this client, file and mapping, then the copy's page. */
  async onCopyFromShared() {
    if (!this.clientId) {
      return;
    }

    let shared: PdfTemplateRow[];
    try {
      shared = (await getClientPdfTemplatesForAdministration(null)).templates;
    } catch {
      SnackbarModule.showError({ key: "admin.pdfTemplates.files.errors.FAILED" });
      return;
    }

    const choice = await this.fileDialog.show({
      mode: "copy",
      title: this.$t("admin.pdfTemplates.files.copyFromShared").toString(),
      initialName: "",
      takenFileNames: this.templates.map((template) => template.fileName ?? ""),
      consequenceLines: this.consequences("copyFromShared"),
      sources: shared.map((template) => ({
        id: template.id,
        label: template.name || template.fileName || template.id,
        fileName: template.fileName ?? "",
      })),
    });
    if (!choice?.sourceId) {
      return;
    }

    const clientId = this.clientId;
    const sourceId = choice.sourceId;
    await this.runAndOpen(() => copyClientPdfTemplateForAdministration(null, sourceId, clientId, choice.fileName));
  }

  /** Runs a change that makes a template, then opens it; IRES Cloud's refusal in the user's terms. */
  async runAndOpen(change: () => Promise<string>) {
    this.busy = true;
    try {
      const templateId = await change();
      SnackbarModule.showSuccess({ key: "admin.pdfTemplates.files.success.created" });
      this.openTemplateById(templateId);
    } catch (error) {
      SnackbarModule.showError({ key: fileOperationErrorKey(error) });
    } finally {
      this.busy = false;
    }
  }

  /** The query that addresses this list: the client, plus `shared=1` for the shared templates. */
  get scopeQuery(): Record<string, string> {
    return {
      ...(this.clientId ? { clientId: this.clientId } : {}),
      ...(this.isShared ? { shared: "1" } : {}),
    };
  }

  openShared() {
    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES,
      query: { ...(this.clientId ? { clientId: this.clientId } : {}), shared: "1" },
    });
  }

  /** Back to the client's own list from the shared one, else to the client detail. */
  onBack() {
    if (this.isShared && this.clientId) {
      router.push({ name: ComponentName.CLIENT_ADMINISTRATION_PDF_TEMPLATES, query: { clientId: this.clientId } });
      return;
    }

    if (this.clientId) {
      router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: this.clientId } });
      return;
    }

    // The shared list opened from the portal page's PDF-Vorlagen tile, without a client.
    router.push({ name: this.isShared ? ComponentName.ADMIN_PORTAL : ComponentName.CLIENT_ADMINISTRATION_LIST });
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;

    try {
      const list = await getClientPdfTemplatesForAdministration(this.isShared ? null : this.clientId);
      this.templates = list.templates;
      this.hyDryCertificateTemplateId = list.hyDryCertificateTemplateId;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud
      // included), and the refresh button is the only useful action.
      this.loadError = true;
      this.templates = [];
      this.hyDryCertificateTemplateId = null;
    } finally {
      this.isLoading = false;
    }
  }

  /** Quiet refresh for a return from a template's page, which may have changed its row. */
  async reloadInBackground() {
    try {
      const list = await getClientPdfTemplatesForAdministration(this.isShared ? null : this.clientId);
      this.templates = list.templates;
      this.hyDryCertificateTemplateId = list.hyDryCertificateTemplateId;
    } catch {
      // Swallowed on purpose: the rows shown stay valid, and the refresh button retries.
    }
  }

  updateToolbarTitle() {
    if (this.isShared) {
      ToolbarContentModule.setI18nResource("admin.pdfTemplates.sharedTitle");
    } else {
      setClientPageToolbarTitle(this.clientId ?? "", this.$t("admin.pdfTemplates.title").toString());
    }
  }

  mounted() {
    this.componentActive = true;
    this.updateToolbarTitle();
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_2);

    // A client list needs its client; without one only the shared list makes sense.
    if (!this.isShared && !this.clientId) {
      this.loadError = true;
      this.isLoading = false;
      return;
    }

    this.load();

    if (!this.isShared && this.clientId) {
      this.loadClientLabel(this.clientId);
    }
  }

  /** Back from the cache, kept per client URL: the rows at once, then a quiet refresh. */
  activated() {
    this.componentActive = true;
    this.updateToolbarTitle();
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_2);
    // Not on the first visit (activated follows mounted), where load() is still running.
    if (!this.isLoading && !this.loadError) {
      this.reloadInBackground();
    }
  }

  deactivated() {
    this.componentActive = false;
  }

  /**
   * For the consequence lines; the client's id stands in when its name cannot be loaded. The cached
   * detail's name shows at once (`AdminReadCache.ts`) and stays if the read fails.
   */
  async loadClientLabel(clientId: string) {
    const cached = cachedClientForAdministration(clientId);
    if (cached) {
      this.clientLabel = clientDetailTitle(cached) || null;
    }
    try {
      this.clientLabel = clientDetailTitle(await getClientForAdministration(clientId)) || null;
      // Opened by a link or reloaded, the app bar did not know the client yet.
      if (this.clientLabel) {
        rememberClientTitle(clientId, this.clientLabel);
        setClientPageToolbarTitle(clientId, this.$t("admin.pdfTemplates.title").toString());
      }
    } catch {
      // Swallowed on purpose: the cached name or the id is shown instead.
      if (!cached) {
        this.clientLabel = null;
      }
    }
  }
}
